import React, { useEffect, useMemo, useState } from 'react';
import { getPublicMatchesApi, getPublicStandingsApi } from '../api/match.api';
import {
  MATCH_STATUS_LABELS,
  MATCH_TYPE_LABELS,
  GENDER_LABELS,
  AGE_GROUP_LABELS,
} from '../utils/constants';
import { toJalali } from '../utils/jalali';
import GradientButton from '../components/common/GradientButton';
import Card from '../components/common/Card';
import StatusBadge from '../components/common/StatusBadge';
import Icon from '../components/common/Icon';
import MatchDetailsModal from '../components/common/MatchDetailsModal';
import TopPlayersCard from '../components/common/TopPlayersCard';

const FILTER_ITEMS = [
  {
    key: 'all',
    label: 'همه مسابقات',
    icon: 'trophy',
    filter: {},
  },
  {
    key: 'upcoming',
    label: 'مسابقات پیش رو',
    icon: 'calendar',
    filter: { status: 'SCHEDULED' },
  },
  {
    key: 'played',
    label: 'نتایج مسابقات',
    icon: 'check',
    filter: { status: 'PLAYED' },
  },
];

function StandingsTable({ rows }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-right text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-4 text-xs font-black text-slate-500">#</th>
              <th className="px-4 py-4 text-xs font-black text-slate-500">باشگاه</th>
              <th className="px-4 py-4 text-center text-xs font-black text-slate-500">بازی</th>
              <th className="px-4 py-4 text-center text-xs font-black text-emerald-600">برد</th>
              <th className="px-4 py-4 text-center text-xs font-black text-amber-600">مساوی</th>
              <th className="px-4 py-4 text-center text-xs font-black text-red-500">باخت</th>
              <th className="px-4 py-4 text-center text-xs font-black text-slate-500">گل زده</th>
              <th className="px-4 py-4 text-center text-xs font-black text-slate-500">گل خورده</th>
              <th className="px-4 py-4 text-center text-xs font-black text-slate-500">تفاضل</th>
              <th className="px-4 py-4 text-center text-xs font-black text-emerald-600">امتیاز</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {rows.map((row, idx) => (
              <tr key={row.club?.id ?? idx} className="transition hover:bg-emerald-50/40">
                <td className="px-4 py-4">
                  <span
                    className={`
                      flex h-8 w-8 items-center justify-center
                      rounded-full text-xs font-black
                      ${
                        idx === 0
                          ? 'bg-amber-100 text-amber-700'
                          : idx === 1
                          ? 'bg-slate-200 text-slate-700'
                          : idx === 2
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-slate-50 text-slate-400'
                      }
                    `}
                  >
                    {idx + 1}
                  </span>
                </td>

                <td className="px-4 py-4 font-bold text-slate-800">
                  {row.club?.name || 'نامشخص'}
                </td>

                <td className="px-4 py-4 text-center">{row.played}</td>
                <td className="px-4 py-4 text-center font-bold text-emerald-600">{row.won}</td>
                <td className="px-4 py-4 text-center font-bold text-amber-600">{row.drawn}</td>
                <td className="px-4 py-4 text-center font-bold text-red-500">{row.lost}</td>
                <td className="px-4 py-4 text-center">{row.goalsFor}</td>
                <td className="px-4 py-4 text-center">{row.goalsAgainst}</td>
                <td className="px-4 py-4 text-center font-mono font-bold">{row.goalDiff}</td>

                <td className="px-4 py-4 text-center">
                  <span className="inline-flex min-w-10 items-center justify-center rounded-xl bg-emerald-50 px-3 py-2 font-black text-emerald-700">
                    {row.points}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Competitions() {
  const [matches, setMatches] = useState([]);
  const [detailsMatch, setDetailsMatch] = useState(null);
  const [filters, setFilters] = useState({
    type: '',
    gender: '',
    ageGroup: '',
    status: '',
  });

  const [activeSection, setActiveSection] = useState('all');

  const [loading, setLoading] = useState(true);

  const [tournamentId, setTournamentId] = useState('');
  const [standings, setStandings] = useState(null);
  const [standingsGroups, setStandingsGroups] = useState(null);
  const [standingsLoading, setStandingsLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    setLoading(true);

    const clean = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== '')
    );

    getPublicMatchesApi({
      ...clean,
      limit: 50,
    })
      .then(({ data }) => {
        if (!mounted) return;
        setMatches(data.data || []);
      })
      .catch(() => {
        if (!mounted) return;
        setMatches([]);
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [filters]);

  const upcoming = useMemo(
    () => matches.filter((m) => m.status === 'SCHEDULED'),
    [matches]
  );

  const played = useMemo(
    () => matches.filter((m) => m.status === 'PLAYED'),
    [matches]
  );

  const activeLabel = useMemo(() => {
    if (activeSection === 'upcoming') return 'مسابقات پیش رو';
    if (activeSection === 'played') return 'نتایج مسابقات';

    return 'همه مسابقات';
  }, [activeSection]);

  const visibleMatches = useMemo(() => {
    if (activeSection === 'upcoming') return upcoming;
    if (activeSection === 'played') return played;

    return matches;
  }, [activeSection, matches, upcoming, played]);

  const handleSectionChange = (section) => {
    setActiveSection(section);

    const item = FILTER_ITEMS.find(
      (entry) => entry.key === section
    );

    setFilters({
      type: '',
      gender: '',
      ageGroup: '',
      status: item?.filter?.status || '',
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));

    setActiveSection('all');
  };

  const fetchStandings = async () => {
    if (!tournamentId) {
      alert('لطفاً شناسه تورنمنت را وارد کنید.');
      return;
    }

    setStandingsLoading(true);
    setStandings(null);
    setStandingsGroups(null);

    try {
      const { data } = await getPublicStandingsApi(tournamentId);
      setStandings(data.data.standings);
      setStandingsGroups(data.data.groups || null);
    } catch {
      alert('تورنمنت یافت نشد یا جدول رده‌بندی موجود نیست.');
    } finally {
      setStandingsLoading(false);
    }
  };

  return (
    <section
      dir="rtl"
      className="container-page py-16 animate-fade-in"
    >
      {/* ================= HEADER ================= */}

      <div className="mb-10">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-emerald-600">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              مرکز مسابقات هیئت فوتبال
            </div>

            <h1 className="text-3xl font-black tracking-tight text-text-dark md:text-4xl">
              مسابقات و نتایج
            </h1>

            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-500 md:text-base">
              برنامه مسابقات، نتایج، وضعیت رقابت‌ ها و جدول
              رده‌بندی تیم‌ها را مشاهده کنید.
            </p>
          </div>

          {/* تعداد مسابقات */}

          {!loading && (
            <div className="flex items-center gap-3">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-3">
                <div className="text-[11px] font-bold text-emerald-500">
                  تعداد مسابقات
                </div>

                <div className="mt-1 text-xl font-black text-emerald-700">
                  {matches.length}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-100 bg-white px-5 py-3 shadow-sm">
                <div className="text-[11px] font-bold text-slate-400">
                  بخش فعال
                </div>

                <div className="mt-1 text-sm font-black text-slate-800">
                  {activeLabel}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ================= MAIN LAYOUT ================= */}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[250px_minmax(0,1fr)]">

        {/* ================= SIDEBAR ================= */}

        <aside className="lg:sticky lg:top-6 lg:h-fit">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">

            {/* Sidebar Header */}

            <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-800 to-emerald-900 p-5 text-white">
              <div className="absolute -left-8 -top-8 h-24 w-24 rounded-full bg-emerald-400/10" />
              <div className="absolute -bottom-10 -right-8 h-28 w-28 rounded-full bg-amber-400/10" />

              <div className="relative flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 backdrop-blur">
                  <Icon
                    name="trophy"
                    className="h-6 w-6 text-amber-300"
                  />
                </div>

                <div>
                  <div className="text-sm font-black">
                    مرکز مسابقات
                  </div>

                  <div className="mt-1 text-[11px] text-white/60">
                    رقابت‌ ها و نتایج
                  </div>
                </div>
              </div>
            </div>

            {/* Main sections */}

            <nav className="p-3">
              <div className="space-y-1">
                {FILTER_ITEMS.map((item) => {
                  const active =
                    activeSection === item.key;

                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() =>
                        handleSectionChange(item.key)
                      }
                      className={`
                        group relative flex w-full items-center
                        gap-3 rounded-2xl px-4 py-3
                        text-right transition-all duration-300
                        ${
                          active
                            ? 'bg-emerald-50 text-emerald-700 shadow-sm'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }
                      `}
                    >
                      {active && (
                        <span className="absolute right-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-l-full bg-emerald-500" />
                      )}

                      <span
                        className={`
                          flex h-10 w-10 shrink-0 items-center
                          justify-center rounded-xl transition-all
                          ${
                            active
                              ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                              : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'
                          }
                        `}
                      >
                        <Icon
                          name={item.icon}
                          className="h-5 w-5"
                        />
                      </span>

                      <span className="flex-1">
                        <span className="block text-sm font-bold">
                          {item.label}
                        </span>

                        {active && (
                          <span className="mt-0.5 block text-[10px] font-medium text-emerald-500">
                            بخش فعال
                          </span>
                        )}
                      </span>

                      <Icon
                        name="chevronLeft"
                        className={`
                          h-4 w-4 transition-all
                          ${
                            active
                              ? 'text-emerald-500'
                              : 'translate-x-1 text-slate-300 group-hover:translate-x-0'
                          }
                        `}
                      />
                    </button>
                  );
                })}
              </div>
            </nav>

            {/* Divider */}

            <div className="mx-4 border-t border-slate-100" />

            {/* Filters */}

            <div className="p-4">
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100">
                  <Icon
                    name="filter"
                    className="h-4 w-4 text-slate-500"
                  />
                </div>

                <div>
                  <div className="text-xs font-black text-slate-700">
                    فیلتر مسابقات
                  </div>

                  <div className="text-[10px] text-slate-400">
                    جستجوی دقیق‌تر
                  </div>
                </div>
              </div>

              <div className="space-y-3">

                {/* Type */}

                <FilterSelect
                  label="نوع مسابقه"
                  value={filters.type}
                  onChange={(value) =>
                    handleFilterChange('type', value)
                  }
                  options={MATCH_TYPE_LABELS}
                />

                {/* Gender */}

                <FilterSelect
                  label="جنسیت"
                  value={filters.gender}
                  onChange={(value) =>
                    handleFilterChange('gender', value)
                  }
                  options={GENDER_LABELS}
                />

                {/* Age */}

                <FilterSelect
                  label="گروه سنی"
                  value={filters.ageGroup}
                  onChange={(value) =>
                    handleFilterChange(
                      'ageGroup',
                      value
                    )
                  }
                  options={AGE_GROUP_LABELS}
                />

                {/* Status */}

                <FilterSelect
                  label="وضعیت"
                  value={filters.status}
                  onChange={(value) =>
                    handleFilterChange(
                      'status',
                      value
                    )
                  }
                  options={MATCH_STATUS_LABELS}
                />
              </div>
            </div>
          </div>
        </aside>

        {/* ================= CONTENT ================= */}

        <main className="min-w-0">

          {/* Active section title */}

          <div className="mb-6 flex items-center justify-between">
            <div>
              <div className="text-xs font-bold text-emerald-600">
                مسابقات
              </div>

              <h2 className="mt-1 text-xl font-black text-slate-900">
                {activeLabel}
              </h2>
            </div>

            <div className="rounded-full bg-slate-100 px-4 py-2 text-xs font-bold text-slate-500">
              {visibleMatches.length} مسابقه
            </div>
          </div>

          {/* ================= LOADING ================= */}

          {loading ? (
            <div className="grid gap-5 md:grid-cols-2">
              {[1, 2, 3, 4].map((item) => (
                <MatchSkeleton key={item} />
              ))}
            </div>
          ) : visibleMatches.length === 0 ? (

            /* ================= EMPTY ================= */

            <div className="flex min-h-[420px] flex-col items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-white px-6 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-slate-100 text-slate-400">
                <Icon
                  name="trophy"
                  className="h-9 w-9"
                />
              </div>

              <h3 className="mt-5 text-lg font-black text-slate-800">
                مسابقه‌ای پیدا نشد
              </h3>

              <p className="mt-2 max-w-md text-sm leading-7 text-slate-400">
                برای این بخش هنوز مسابقه‌ای ثبت نشده
                یا با فیلترهای انتخاب‌ شده نتیجه‌ای وجود ندارد.
              </p>

              {(filters.type ||
                filters.gender ||
                filters.ageGroup ||
                filters.status) && (
                <button
                  type="button"
                  onClick={() => {
                    setFilters({
                      type: '',
                      gender: '',
                      ageGroup: '',
                      status: '',
                    });

                    setActiveSection('all');
                  }}
                  className="mt-5 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-emerald-500/20 transition hover:bg-emerald-600"
                >
                  حذف فیلترها
                </button>
              )}
            </div>

          ) : (

            /* ================= MATCH CARDS ================= */

            <div className="grid gap-5 md:grid-cols-2">
              {visibleMatches.map((match) => (
                <MatchCard
                  key={match.id}
                  match={match}
                  onDetails={setDetailsMatch}
                />
              ))}
            </div>
          )}

          <MatchDetailsModal
            open={Boolean(detailsMatch)}
            onClose={() => setDetailsMatch(null)}
            matchId={detailsMatch?.id}
            initialMatch={detailsMatch}
            isPublic
          />

          {/* ================= TOP PLAYERS ================= */}

          <div className="mt-10">
            <TopPlayersCard />
          </div>

          {/* ================= STANDINGS ================= */}

          <div className="mt-10">
            <Card className="overflow-hidden rounded-3xl border border-slate-200 bg-white p-0">

              {/* Standings Header */}

              <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-800 to-emerald-900 px-6 py-7 text-white">
                <div className="absolute -left-10 -top-16 h-40 w-40 rounded-full bg-amber-400/10" />
                <div className="absolute -bottom-16 right-10 h-40 w-40 rounded-full bg-emerald-400/10" />

                <div className="relative flex items-center gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10">
                    <Icon
                      name="trophy"
                      className="h-7 w-7 text-amber-300"
                    />
                  </div>

                  <div>
                    <h2 className="text-xl font-black">
                      جدول رده‌بندی
                    </h2>

                    <p className="mt-1 text-xs text-white/60">
                      مشاهده جدول امتیازات تورنمنت
                    </p>
                  </div>
                </div>
              </div>

              {/* Search tournament */}

              <div className="p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">

                  <div className="flex-1">
                    <label className="mb-2 block text-xs font-bold text-slate-500">
                      شناسه تورنمنت
                    </label>

                    <input
                      type="number"
                      value={tournamentId}
                      onChange={(e) =>
                        setTournamentId(e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          fetchStandings();
                        }
                      }}
                      placeholder="مثلاً 1"
                      className="h-12 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 px-4 text-sm font-medium outline-none transition-all focus:border-emerald-500 focus:bg-white focus:shadow-lg focus:shadow-emerald-500/10"
                    />
                  </div>

                  <GradientButton
                    onClick={fetchStandings}
                    disabled={standingsLoading}
                  >
                    {standingsLoading
                      ? 'در حال دریافت...'
                      : 'نمایش جدول'}
                  </GradientButton>
                </div>

                {standingsLoading && (
                  <div className="mt-8 flex justify-center">
                    <div className="h-7 w-7 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
                  </div>
                )}

                {(standingsGroups?.length > 0 || standings?.length > 0) && (
                  <div className="mt-8 space-y-8">
                    {standingsGroups?.length > 0
                      ? standingsGroups.map((group) => (
                          <div key={group.id}>
                            <h3 className="mb-3 flex items-center gap-2 text-sm font-black text-slate-700">
                              <Icon name="medal" className="h-4 w-4 text-blue-500" />
                              {group.name}
                              {group.stageName && (
                                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold text-slate-400">
                                  {group.stageName}
                                </span>
                              )}
                            </h3>
                            {group.standings?.length > 0 ? (
                              <StandingsTable rows={group.standings} />
                            ) : (
                              <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">
                                هنوز مسابقه‌ای در این گروه برگزار نشده است.
                              </div>
                            )}
                          </div>
                        ))
                      : null}

                    {standings?.length > 0 && (
                      <div>
                        {standingsGroups?.length > 0 && (
                          <h3 className="mb-3 flex items-center gap-2 text-sm font-black text-slate-700">
                            <Icon name="userGroup" className="h-4 w-4 text-slate-400" />
                            بدون گروه
                          </h3>
                        )}
                        <StandingsTable rows={standings} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>
        </main>
      </div>
    </section>
  );
}

/* =========================================================
   FILTER SELECT
========================================================= */

function FilterSelect({
  label,
  value,
  onChange,
  options,
}) {
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-bold text-slate-400">
        {label}
      </label>

      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="
            h-10 w-full appearance-none rounded-xl
            border border-slate-200 bg-slate-50
            px-3 pl-8 text-xs font-bold text-slate-600
            outline-none transition-all
            hover:border-slate-300
            focus:border-emerald-500
            focus:bg-white
            focus:ring-4
            focus:ring-emerald-500/10
          "
        >
          <option value="">
            همه
          </option>

          {Object.entries(options).map(([key, label]) => (
            <option
              key={key}
              value={key}
            >
              {label}
            </option>
          ))}
        </select>

        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
          <Icon
            name="chevronDown"
            className="h-4 w-4"
          />
        </span>
      </div>
    </div>
  );
}

/* =========================================================
   PLAYER STATS (scorers / cards)
========================================================= */

function MatchPlayerStats({ playerStatistics }) {
  if (!playerStatistics || playerStatistics.length === 0) return null;

  const withStats = playerStatistics.filter(
    (s) => s.goals > 0 || s.assists > 0 || s.yellowCards > 0 || s.redCards > 0
  );

  if (withStats.length === 0) return null;

  return (
    <div className="mt-4 flex flex-wrap justify-center gap-1.5 border-t border-slate-100 pt-4">
      {withStats.map((s) => {
        const name = `${s.player?.firstName || ''} ${s.player?.lastName || ''}`.trim();
        const parts = [];

        if (s.goals > 0) parts.push(`⚽×${s.goals}`);
        if (s.assists > 0) parts.push(`🎯×${s.assists}`);
        if (s.yellowCards > 0) parts.push('🟨');
        if (s.redCards > 0) parts.push('🟥');

        return (
          <span
            key={s.id}
            className="rounded-full bg-slate-50 px-2.5 py-1 text-[10px] font-bold text-slate-600 ring-1 ring-slate-100"
          >
            {name} {parts.join(' ')}
          </span>
        );
      })}
    </div>
  );
}

/* =========================================================
   MATCH CARD
========================================================= */

function MatchCard({ match, onDetails }) {
  const isPlayed = match.status === 'PLAYED';

  const statusLabel =
    MATCH_STATUS_LABELS[match.status] ||
    match.status;

  return (
    <Card
      hover
      className="
        group relative overflow-hidden rounded-3xl
        border border-slate-100 bg-white p-0
        transition-all duration-500
        hover:-translate-y-1
      "
    >
      {/* Top accent */}

      <div
        className={`
          h-1 w-full
          ${
            isPlayed
              ? 'bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500'
              : 'bg-gradient-to-r from-amber-300 via-emerald-400 to-emerald-600'
          }
        `}
      />

      <div className="p-5">

        {/* Meta */}

        <div className="mb-5 flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {match.type && (
              <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-bold text-slate-500">
                {MATCH_TYPE_LABELS[match.type] ||
                  match.type}
              </span>
            )}

            {match.gender && (
              <span className="rounded-full bg-blue-50 px-3 py-1 text-[10px] font-bold text-blue-500">
                {GENDER_LABELS[match.gender] ||
                  match.gender}
              </span>
            )}
          </div>

          <StatusBadge
            status={isPlayed ? 'success' : 'pending'}
            label={statusLabel}
          />
        </div>

        {/* Teams */}

        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">

          {/* Home */}

          <div className="min-w-0 text-center">
            <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-50 ring-1 ring-slate-100">
              {match.homeClub?.logo ? (
                <img
                  src={match.homeClub.logo}
                  alt={match.homeClub.name}
                  className="h-12 w-12 object-contain"
                />
              ) : (
                <Icon
                  name="shield"
                  className="h-8 w-8 text-slate-300"
                />
              )}
            </div>

            <div className="truncate text-sm font-black text-slate-800">
              {match.homeClub?.name || 'میزبان'}
            </div>

            <div className="mt-1 text-[10px] text-slate-400">
              میزبان
            </div>
          </div>

          {/* Score */}

          <div className="flex flex-col items-center">
            {isPlayed ? (
              <div
                dir="ltr"
                className="rounded-2xl bg-slate-900 px-4 py-3 text-xl font-black tracking-wider text-white shadow-lg"
              >
                {match.result?.awayScore ?? 0}
                <span className="mx-2 text-slate-500">
                  -
                </span>
                {match.result?.homeScore ?? 0}
              </div>
            ) : (
              <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-white px-4 py-3 text-sm font-black text-emerald-600 ring-1 ring-emerald-100">
                VS
              </div>
            )}

            {match.date && (
              <div className="mt-2 text-[10px] font-bold text-slate-400">
                {toJalali(match.date)}
              </div>
            )}
          </div>

          {/* Away */}

          <div className="min-w-0 text-center">
            <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-50 ring-1 ring-slate-100">
              {match.awayClub?.logo ? (
                <img
                  src={match.awayClub.logo}
                  alt={match.awayClub.name}
                  className="h-12 w-12 object-contain"
                />
              ) : (
                <Icon
                  name="shield"
                  className="h-8 w-8 text-slate-300"
                />
              )}
            </div>

            <div className="truncate text-sm font-black text-slate-800">
              {match.awayClub?.name || 'مهمان'}
            </div>

            <div className="mt-1 text-[10px] text-slate-400">
              مهمان
            </div>
          </div>
        </div>

        {/* Match details */}

        <div className="mt-6 grid grid-cols-2 gap-2 border-t border-slate-100 pt-4">

          <div className="rounded-xl bg-slate-50 px-3 py-2.5">
            <div className="text-[10px] font-bold text-slate-400">
              زمان
            </div>

            <div className="mt-1 text-xs font-black text-slate-700">
              {match.time || '--:--'}
            </div>
          </div>

          <div className="rounded-xl bg-slate-50 px-3 py-2.5">
            <div className="text-[10px] font-bold text-slate-400">
              محل برگزاری
            </div>

            <div
              className="mt-1 truncate text-xs font-black text-slate-700"
              title={match.location || ''}
            >
              {match.location || 'نامشخص'}
            </div>
          </div>
        </div>

        {/* Scorers / Cards */}
        {isPlayed && (
          <MatchPlayerStats playerStatistics={match.playerStatistics} />
        )}

        {onDetails && (
          <button
            type="button"
            onClick={() => onDetails(match)}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-50 px-4 py-2.5 text-xs font-black text-emerald-700 transition hover:bg-emerald-500 hover:text-white"
          >
            <Icon name="eye" className="h-4 w-4" />
            مشاهده‌ی جزئیات کامل بازی
          </button>
        )}
      </div>
    </Card>
  );
}

/* =========================================================
   SKELETON
========================================================= */

function MatchSkeleton() {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white">
      <div className="h-1 animate-pulse bg-slate-100" />

      <div className="p-5">
        <div className="mb-5 flex justify-between">
          <div className="h-6 w-24 animate-pulse rounded-full bg-slate-100" />
          <div className="h-6 w-20 animate-pulse rounded-full bg-slate-100" />
        </div>

        <div className="grid grid-cols-3 items-center gap-3">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 animate-pulse rounded-2xl bg-slate-100" />
            <div className="mx-auto mt-3 h-4 w-24 animate-pulse rounded bg-slate-100" />
          </div>

          <div className="mx-auto h-12 w-16 animate-pulse rounded-2xl bg-slate-100" />

          <div className="text-center">
            <div className="mx-auto h-16 w-16 animate-pulse rounded-2xl bg-slate-100" />
            <div className="mx-auto mt-3 h-4 w-24 animate-pulse rounded bg-slate-100" />
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-2">
          <div className="h-12 animate-pulse rounded-xl bg-slate-100" />
          <div className="h-12 animate-pulse rounded-xl bg-slate-100" />
        </div>
      </div>
    </div>
  );
}